import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Loader, AlertTriangle, X } from 'lucide-react';
import {
  getUploadUrl,
  uploadFileToS3,
  getUploadStatusList,
  getUploadStatuses,
  FILE_TYPES,
  UploadRejectedError,
  UploadStatusList,
  UploadStatusRow,
  RebuildStatusRow,
  SUPPORT_EMAIL,
  folderLabel,
  nameMatchesFolder,
  suggestFolderForName,
  exampleName,
  fileTypeProblem,
  fileSizeProblem,
  ACCEPT_ATTR,
  MAX_XLSX_MB,
  MAX_XLSX_ROWS,
} from '../services/uploadService';
import { NavBar } from './NavBar';

// ─── Types and small helpers ───────────────────────────────────────────────────

type ItemState =
  | 'uploading'   // being sent
  | 'checking'    // sent; waiting for the pipeline's verdict
  | 'slow'        // no verdict after 60 s
  | 'rejected'    // refused before upload (name or folder); nothing was uploaded
  | 'error'       // the transfer itself failed
  | 'done';       // verdict received; the result now lives in "Recent uploads"

interface SessionItem {
  id: number;
  file: File;
  filename: string;
  folder: string;
  state: ItemState;
  progress: number;
  message?: string;
  suggestedFolder?: string;
  serverTimeMs?: number;
  startedAtMs?: number;
}

const FAST_POLL_MS = 3000;          // first minute after an upload
const SLOW_POLL_MS = 15000;
const SLOW_AFTER_MS = 60000;
const GIVE_UP_AFTER_MS = 30 * 60000;
const LIST_REFRESH_MS = 20000;
const STUCK_AFTER_MS = 15 * 60000;
const DISMISSED_KEY = 'metrix.dismissedUploadRows';
const RECENT_PAGE = 15;

const isInFlight = (s: ItemState) => s === 'uploading' || s === 'checking' || s === 'slow';

function parseMs(iso?: string | null): number | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : ms;
}

function formatTime(iso?: string | null): string {
  const ms = parseMs(iso);
  return ms === undefined ? '' : new Date(ms).toLocaleString();
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatPeriod(year: string, month: string): string {
  const n = Number(month);
  if (/^\d{1,2}$/.test(month) && n >= 1 && n <= 12) return `${MONTH_NAMES[n - 1]} ${year}`;
  const text = month.charAt(0).toUpperCase() + month.slice(1, 3).toLowerCase();
  return `${text} ${year}`;
}

function supportHref(subject: string, detail: string): string {
  const body = `${detail}\n\n(Please do not include sensitive data.)`;
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function needsSupport(message?: string): boolean {
  return !!message && /contact support|could not be queued/i.test(message);
}

function loadDismissed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x: unknown) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(set: Set<string>) {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(set).slice(-300)));
  } catch {
    /* storage unavailable: dismissals last until the page is closed */
  }
}

const uploadRowKey = (r: UploadStatusRow) => `u|${r.filename}|${r.updated_at ?? ''}`;
const rebuildRowKey = (r: RebuildStatusRow) => `r|${r.year}|${r.month}|${r.kind}|${r.updated_at ?? ''}`;

// ─── Page ──────────────────────────────────────────────────────────────────────

export function UploadFiles() {
  const [selectedFileType, setSelectedFileType] = useState('tickets');
  const [items, setItems] = useState<SessionItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [recent, setRecent] = useState<UploadStatusList | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissed());
  const [showAll, setShowAll] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<SessionItem[]>([]);
  const nextId = useRef(1);
  const mounted = useRef(true);

  itemsRef.current = items;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const updateItem = useCallback((id: number, patch: Partial<SessionItem>) => {
    if (!mounted.current) return;
    setItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  // ── Recent uploads (what the pipeline decided, kept on the server) ──
  const refreshList = useCallback(async () => {
    try {
      const result = await getUploadStatusList();
      if (!mounted.current) return;
      setRecent(result);
      setListFailed(false);
    } catch (err) {
      console.warn('Could not refresh upload status', err);
      if (mounted.current) setListFailed(true);
    }
  }, []);

  useEffect(() => {
    refreshList();
    const timer = window.setInterval(() => {
      if (!document.hidden) refreshList();
    }, LIST_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [refreshList]);

  // ── Waiting for the pipeline's verdict on files sent in this visit ──
  const watching = items.some(i => i.state === 'checking' || i.state === 'slow');

  useEffect(() => {
    if (!watching) return;
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      const waiting = itemsRef.current.filter(i => i.state === 'checking' || i.state === 'slow');
      let nextDelay = waiting.length > 0 ? SLOW_POLL_MS : FAST_POLL_MS;
      if (waiting.length > 0) {
        try {
          const result = await getUploadStatuses(Array.from(new Set(waiting.map(i => i.filename))));
          if (cancelled) return;
          const byName = new Map<string, UploadStatusRow>();
          result.uploads.forEach(u => byName.set(u.filename, u));
          const now = Date.now();
          const resolved: number[] = [];
          const slow: number[] = [];
          waiting.forEach(i => {
            const row = byName.get(i.filename);
            const rowMs = parseMs(row?.updated_at);
            const isCurrent = !!row && rowMs !== undefined && rowMs >= (i.serverTimeMs ?? 0);  // not the previous attempt
            const age = now - (i.startedAtMs ?? now);
            if (isCurrent && row && (row.status === 'ingested' || row.status === 'failed')) resolved.push(i.id);
            else if (age > GIVE_UP_AFTER_MS) resolved.push(i.id);   // the Recent list keeps watching
            else if (age > SLOW_AFTER_MS && i.state === 'checking') slow.push(i.id);
            // poll every 3 s in the first minute, landing exactly on the 60 s mark; every 15 s afterwards
            const wanted = age > SLOW_AFTER_MS ? SLOW_POLL_MS : Math.min(FAST_POLL_MS, SLOW_AFTER_MS - age + 50);
            nextDelay = Math.min(nextDelay, wanted);
          });
          if (resolved.length > 0) {
            await refreshList();             // show the result before the progress row disappears
            if (cancelled) return;
          }
          if (resolved.length > 0 || slow.length > 0) {
            setItems(prev => prev.map(i => {
              if (resolved.includes(i.id)) return { ...i, state: 'done' as ItemState };
              if (slow.includes(i.id)) return { ...i, state: 'slow' as ItemState };
              return i;
            }));
          }
        } catch (err) {
          console.warn('Status check failed; will retry', err);
          nextDelay = SLOW_POLL_MS;
        }
      }
      if (!cancelled) timer = window.setTimeout(tick, nextDelay);
    };

    timer = window.setTimeout(tick, FAST_POLL_MS);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [watching, refreshList]);

  // ── Sending files ──
  const sendItem = useCallback(async (item: SessionItem) => {
    updateItem(item.id, { state: 'uploading', progress: 0, message: undefined, suggestedFolder: undefined, folder: item.folder });
    try {
      const { uploadUrl, serverTime, contentType } = await getUploadUrl(item.filename, item.folder);
      await uploadFileToS3(item.file, uploadUrl, progress => updateItem(item.id, { progress }), contentType);
      updateItem(item.id, {
        state: 'checking',
        progress: 100,
        serverTimeMs: parseMs(serverTime) ?? Date.now(),
        startedAtMs: Date.now(),
      });
    } catch (error: any) {
      if (error instanceof UploadRejectedError) {
        updateItem(item.id, { state: 'rejected', message: error.message, suggestedFolder: error.suggestedFolder });
      } else {
        console.error(`Upload failed for ${item.filename}:`, error);
        updateItem(item.id, { state: 'error', message: error?.message || 'Upload failed' });
      }
    }
  }, [updateItem]);

  const handleFiles = async (files: File[]) => {
    if (files.length === 0) return;
    const folder = selectedFileType;
    const created: SessionItem[] = files.map(file => {
      const base: SessionItem = {
        id: nextId.current++,
        file,
        filename: file.name,
        folder,
        state: 'uploading',
        progress: 0,
      };
      const typeProblem = fileTypeProblem(file.name) ?? fileSizeProblem(file);
      if (typeProblem) {
        return { ...base, state: 'rejected', message: typeProblem };
      }
      // Right name, wrong folder: the most common mistake. Say so before anything is sent.
      if (!nameMatchesFolder(file.name, folder)) {
        const other = suggestFolderForName(file.name, folder);
        if (other) {
          return {
            ...base,
            state: 'rejected',
            suggestedFolder: other,
            message: `This looks like a "${folderLabel(other)}" file — upload it to "${folderLabel(other)}" instead?`,
          };
        }
      }
      return base;
    });

    setItems(prev => [...prev, ...created]);
    for (const item of created) {
      if (item.state === 'uploading') {
        await sendItem(item);
      }
    }
  };

  const reroute = async (itemIds: number[], folder: string) => {
    const targets = itemsRef.current.filter(i => itemIds.includes(i.id) && i.state === 'rejected');
    for (const t of targets) {
      await sendItem({ ...t, folder });
    }
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(Array.from(e.target.files));
    e.target.value = '';   // allow choosing the same file again
  };

  const dismissItem = (id: number) => setItems(prev => prev.filter(i => i.id !== id));
  const clearFinished = () => setItems(prev => prev.filter(i => isInFlight(i.state)));

  const dismissRow = (key: string) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(key);
      saveDismissed(next);
      return next;
    });
  };

  // ── Derived display data ──
  const visibleItems = items.filter(i => i.state !== 'done');
  const rejectedBySuggestion = new Map<string, number[]>();
  visibleItems.forEach(i => {
    if (i.state === 'rejected' && i.suggestedFolder) {
      rejectedBySuggestion.set(i.suggestedFolder, [...(rejectedBySuggestion.get(i.suggestedFolder) ?? []), i.id]);
    }
  });

  const serverNowMs = parseMs(recent?.serverTime) ?? Date.now();
  const uploadRows = (recent?.uploads ?? []).filter(r => !dismissed.has(uploadRowKey(r)));
  const shownUploadRows = showAll ? uploadRows : uploadRows.slice(0, RECENT_PAGE);
  const rebuildRows = (recent?.rebuilds ?? []).filter(r => {
    if (dismissed.has(rebuildRowKey(r))) return false;
    if (r.status === 'failed') return true;
    if (r.status === 'queued' || r.status === 'processing') {
      const ms = parseMs(r.updated_at);
      return ms !== undefined && serverNowMs - ms < 30 * 60000;   // hide rows that were simply abandoned long ago
    }
    return false;
  });

  const selected = FILE_TYPES.find(t => t.value === selectedFileType);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <div className="max-w-4xl mx-auto p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Files</h2>
          <p className="text-gray-600">
            Upload CSV or Excel (.xlsx) files for processing. Select a file type and drag-and-drop or click to browse.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <label htmlFor="file-type" className="block text-sm font-medium text-gray-700 mb-2">
            File Type
          </label>
          <select
            id="file-type"
            value={selectedFileType}
            onChange={(e) => setSelectedFileType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {FILE_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          <details className="mt-4 text-sm text-gray-700">
            <summary className="cursor-pointer font-medium text-gray-800">File naming rules</summary>
            <div className="mt-2 space-y-1">
              <p>
                Name for this folder: <code className="bg-gray-100 px-1 rounded">{exampleName(selectedFileType)}</code>
              </p>
              {selected?.kind === 'period' ? (
                <>
                  <p>
                    Format: <code className="bg-gray-100 px-1 rounded">{'{source-id}'}-{selected.code}-{'{mon}{yyyy}'}.csv</code> (or <code className="bg-gray-100 px-1 rounded">.xlsx</code>).
                    The month is three letters (jan, feb, &hellip; dec) and the year has four digits.
                  </p>
                  <p>
                    To replace a file you already uploaded, add a version: <code className="bg-gray-100 px-1 rounded">acs-{selected.code}-aug2026-v2.csv</code> (v2 or higher; the same ending applies to .xlsx).
                  </p>
                </>
              ) : (
                <p>
                  Format: <code className="bg-gray-100 px-1 rounded">{'{source-id}'}-{selected?.code}.csv</code> (or <code className="bg-gray-100 px-1 rounded">.xlsx</code>). These files have no
                  month or version. To replace one, upload the complete file again under the same name.
                </p>
              )}
              <p>
                The source-id says where the file came from (2 to 20 letters, numbers or hyphens). Capital letters are fine
                in the name, but the extension must be lowercase: <code className="bg-gray-100 px-1 rounded">.csv</code> or <code className="bg-gray-100 px-1 rounded">.xlsx</code>,
                not <code className="bg-gray-100 px-1 rounded">.CSV</code> or <code className="bg-gray-100 px-1 rounded">.XLSX</code>.
              </p>
              <p className="font-medium text-gray-800 pt-1">Excel files</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Save as <code className="bg-gray-100 px-1 rounded">.xlsx</code>. The older <code className="bg-gray-100 px-1 rounded">.xls</code> format and macro files (<code className="bg-gray-100 px-1 rounded">.xlsm</code>) are not accepted.</li>
                <li>Only the first visible sheet is read; other and hidden sheets are ignored.</li>
                <li>The header row must be row 1 of that sheet. Blank rows and columns are ignored.</li>
                <li>Formulas are fine if the file was saved by Excel or another spreadsheet program. Open and save a file that was created by a script.</li>
                <li>Keep identifier columns (account numbers, route numbers, codes) as <strong>text</strong>, so leading zeros such as 060343 are kept. Format the column as Text before pasting.</li>
                <li>Maximum {MAX_XLSX_MB} MB and about {MAX_XLSX_ROWS.toLocaleString('en-US')} rows. For larger files, split them or upload as CSV.</li>
              </ul>
            </div>
          </details>
        </div>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            bg-white rounded-lg shadow-sm border-2 border-dashed p-12 text-center cursor-pointer
            transition-all duration-200
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          `}
        >
          <Upload className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className="text-lg font-medium text-gray-900 mb-2">
            Drop CSV or Excel files here or click to browse
          </p>
          <p className="text-sm text-gray-600">
            Supports multiple file upload
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {visibleItems.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">This visit</h3>
              <button onClick={clearFinished} className="text-sm text-gray-600 hover:text-gray-900">
                Clear
              </button>
            </div>

            {Array.from(rejectedBySuggestion.entries()).filter(([, ids]) => ids.length > 1).map(([folder, ids]) => (
              <button
                key={folder}
                onClick={() => reroute(ids, folder)}
                className="mb-3 mr-2 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Upload all {ids.length} to &ldquo;{folderLabel(folder)}&rdquo;
              </button>
            ))}

            <div className="space-y-3">
              {visibleItems.map(item => (
                <SessionRow
                  key={item.id}
                  item={item}
                  onDismiss={() => dismissItem(item.id)}
                  onReroute={folder => reroute([item.id], folder)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-semibold text-gray-900">Recent uploads</h3>
            <button onClick={refreshList} className="text-sm text-gray-600 hover:text-gray-900">
              Refresh
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Files are checked after they arrive. A file that is not accepted stays here, with the reason, until you dismiss it
            or upload a corrected file under the same name.
          </p>

          {recent === null && !listFailed && <p className="text-sm text-gray-500">Loading&hellip;</p>}
          {recent === null && listFailed && (
            <p className="text-sm text-gray-600">Recent upload results are not available right now. Please try again in a moment.</p>
          )}
          {recent !== null && listFailed && (
            <p className="text-xs text-amber-700 mb-3">Could not refresh just now; showing the last results received.</p>
          )}

          {recent !== null && uploadRows.length === 0 && rebuildRows.length === 0 && (
            <p className="text-sm text-gray-500">No recent uploads.</p>
          )}

          <div className="space-y-2">
            {rebuildRows.map(r => (
              <RebuildRow key={rebuildRowKey(r)} row={r} onDismiss={() => dismissRow(rebuildRowKey(r))} />
            ))}
            {shownUploadRows.map(r => (
              <ResultRow
                key={uploadRowKey(r)}
                row={r}
                serverNowMs={serverNowMs}
                onDismiss={() => dismissRow(uploadRowKey(r))}
              />
            ))}
          </div>

          {uploadRows.length > RECENT_PAGE && (
            <button onClick={() => setShowAll(v => !v)} className="mt-3 text-sm text-blue-600 hover:text-blue-800">
              {showAll ? 'Show fewer' : `Show all ${uploadRows.length}`}
            </button>
          )}
          {recent?.truncated && (
            <p className="mt-3 text-xs text-gray-500">Only part of the history could be loaded.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Rows ──────────────────────────────────────────────────────────────────────
// Everything below renders server-supplied text as plain text (React escapes it);
// nothing is ever inserted as HTML.

function SupportLink({ subject, detail }: { subject: string; detail: string }) {
  return (
    <a
      href={supportHref(subject, detail)}
      className="inline-block mt-1 text-xs font-medium text-blue-700 underline hover:text-blue-900"
    >
      Contact support
    </a>
  );
}

function SessionRow({
  item,
  onDismiss,
  onReroute,
}: {
  item: SessionItem;
  onDismiss: () => void;
  onReroute: (folder: string) => void;
}) {
  const failedState = item.state === 'rejected' || item.state === 'error';
  return (
    <div className="border border-gray-200 rounded-lg p-4" role="status">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3 min-w-0">
          <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900 truncate">{item.filename}</span>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {item.state === 'uploading' && <Loader className="w-5 h-5 text-blue-500 animate-spin" />}
          {(item.state === 'checking') && <Loader className="w-5 h-5 text-blue-500 animate-spin" />}
          {item.state === 'slow' && <AlertTriangle className="w-5 h-5 text-amber-500" />}
          {failedState && <XCircle className="w-5 h-5 text-red-500" />}
          {failedState && (
            <button onClick={onDismiss} aria-label={`Dismiss ${item.filename}`} className="text-gray-400 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {item.state === 'uploading' && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${item.progress}%` }} />
        </div>
      )}

      {item.state === 'checking' && <p className="text-xs text-blue-700 mt-1">Checking your file&hellip;</p>}

      {item.state === 'slow' && (
        <p className="text-xs text-amber-700 mt-1">
          Still processing &mdash; check back shortly. The result will appear under Recent uploads.
        </p>
      )}

      {failedState && (
        <>
          <p className="text-xs text-red-600 mt-1">
            {item.state === 'rejected' ? 'Not uploaded: ' : 'Upload failed: '}
            {item.message}
          </p>
          {item.suggestedFolder && (
            <button
              onClick={() => onReroute(item.suggestedFolder as string)}
              className="mt-2 px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Upload to &ldquo;{folderLabel(item.suggestedFolder)}&rdquo;
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ResultRow({
  row,
  serverNowMs,
  onDismiss,
}: {
  row: UploadStatusRow;
  serverNowMs: number;
  onDismiss: () => void;
}) {
  const updatedMs = parseMs(row.updated_at);
  const stuck = row.status === 'processing' && updatedMs !== undefined && serverNowMs - updatedMs > STUCK_AFTER_MS;

  let box = 'border-gray-200 bg-white';
  let icon = <FileText className="w-5 h-5 text-gray-400" />;
  let text: React.ReactNode = null;

  if (row.status === 'failed') {
    box = 'border-red-200 bg-red-50';
    icon = <XCircle className="w-5 h-5 text-red-500" />;
    text = (
      <>
        <p className="text-xs text-red-700 mt-1">Upload failed: {row.last_error}</p>
        {needsSupport(row.last_error) && (
          <SupportLink subject={`Upload failed: ${row.filename}`} detail={`File: ${row.filename}\nMessage: ${row.last_error ?? ''}`} />
        )}
      </>
    );
  } else if (row.status === 'ingested') {
    box = 'border-green-200 bg-green-50';
    icon = <CheckCircle className="w-5 h-5 text-green-600" />;
    text = <p className="text-xs text-green-700 mt-1">Accepted. Reports will update in a few minutes.</p>;
  } else if (row.status === 'processing') {
    box = stuck ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50';
    icon = stuck ? <AlertTriangle className="w-5 h-5 text-amber-500" /> : <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
    text = stuck ? (
      <>
        <p className="text-xs text-amber-800 mt-1">This is taking longer than expected.</p>
        <SupportLink subject={`Upload still processing: ${row.filename}`} detail={`File: ${row.filename}\nLast update: ${row.updated_at ?? ''}`} />
      </>
    ) : (
      <p className="text-xs text-blue-700 mt-1">Checking your file&hellip;</p>
    );
  } else {
    text = <p className="text-xs text-gray-600 mt-1">Status not available.</p>;
  }

  return (
    <div className={`border rounded-lg p-3 ${box}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 min-w-0">
          <div className="mt-0.5 flex-shrink-0">{icon}</div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 break-all">{row.filename}</p>
            <p className="text-xs text-gray-500">{formatTime(row.updated_at)}</p>
            {text}
          </div>
        </div>
        <button onClick={onDismiss} aria-label={`Dismiss ${row.filename}`} className="text-gray-400 hover:text-gray-700 flex-shrink-0 ml-2">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function RebuildRow({ row, onDismiss }: { row: RebuildStatusRow; onDismiss: () => void }) {
  const period = formatPeriod(row.year, row.month);
  const what = row.kind === 'inbound' ? 'inbound reports' : 'outbound reports';
  const failed = row.status === 'failed';
  return (
    <div className={`border rounded-lg p-3 ${failed ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 min-w-0">
          <div className="mt-0.5 flex-shrink-0">
            {failed ? <XCircle className="w-5 h-5 text-red-500" /> : <Loader className="w-5 h-5 text-blue-500 animate-spin" />}
          </div>
          <div className="min-w-0">
            {failed ? (
              <>
                <p className="text-sm font-medium text-gray-900">Updating {what} for {period} failed</p>
                <p className="text-xs text-red-700 mt-1">{row.last_error}</p>
                <SupportLink
                  subject={`Report update failed: ${period} ${what}`}
                  detail={`Period: ${period}\nReports: ${what}\nMessage: ${row.last_error ?? ''}`}
                />
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-gray-900">Updating {what} for {period}&hellip;</p>
                <p className="text-xs text-blue-700 mt-1">{row.current_step || 'This takes a few minutes.'}</p>
              </>
            )}
            <p className="text-xs text-gray-500">{formatTime(row.updated_at)}</p>
          </div>
        </div>
        {failed && (
          <button onClick={onDismiss} aria-label="Dismiss" className="text-gray-400 hover:text-gray-700 flex-shrink-0 ml-2">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
